import React, { useEffect, useRef } from 'react';
import { 
  Edit3, 
  Copy, 
  Clipboard, 
  Trash2, 
  Layers,
  ArrowUp,
  ArrowDown,
  Lock,
  Unlock,
  Eye,
  EyeOff
} from 'lucide-react';

interface ContextMenuProps {
  isOpen: boolean;
  position: { x: number; y: number };
  onClose: () => void;
  onEdit: () => void;
  onCopy: () => void;
  onPaste: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onBringToFront: () => void;
  onSendToBack: () => void;
  onLock: () => void;
  onUnlock: () => void;
  onHide: () => void;
  onShow: () => void;
  canPaste: boolean;
  isLocked: boolean;
  isHidden: boolean;
  selectedCount: number;
  /** User can edit block text/styles (editor.content level) */
  hasEditorContent?: boolean;
  /** User can add/delete/duplicate blocks (editor.full level) */
  hasEditorFull?: boolean;
  /** User can reorder/hide blocks (editor.layout level) */
  hasEditorLayout?: boolean;
}

const ContextMenu: React.FC<ContextMenuProps> = ({
  isOpen,
  position,
  onClose,
  onEdit,
  onCopy,
  onPaste,
  onDuplicate,
  onDelete,
  onBringToFront,
  onSendToBack,
  onLock,
  onUnlock,
  onHide,
  onShow,
  canPaste,
  isLocked,
  isHidden,
  selectedCount,
  hasEditorContent = true,
  hasEditorFull = true,
  hasEditorLayout = true,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Build menu items gated by permission level:
  // - Edit/Copy: requires hasEditorContent
  // - Paste/Duplicate/Delete: requires hasEditorFull
  // - Bring to Front/Send to Back/Lock/Hide: requires hasEditorLayout
  const menuItems: Array<Record<string, unknown>> = [];

  // Edit — requires content editing
  if (hasEditorContent) {
    menuItems.push({
      icon: Edit3,
      label: 'Edit',
      action: onEdit,
      shortcut: 'Enter',
      disabled: selectedCount !== 1,
    });
  }

  // Copy — requires content level
  if (hasEditorContent) {
    menuItems.push({ type: 'separator' });
    menuItems.push({
      icon: Copy,
      label: 'Copy',
      action: onCopy,
      shortcut: 'Ctrl+C',
      disabled: selectedCount === 0,
    });
  }

  // Paste/Duplicate — requires full control
  if (hasEditorFull) {
    menuItems.push({
      icon: Clipboard,
      label: 'Paste',
      action: onPaste,
      shortcut: 'Ctrl+V',
      disabled: !canPaste,
    });
    menuItems.push({
      icon: Layers,
      label: 'Duplicate',
      action: onDuplicate,
      shortcut: 'Ctrl+D',
      disabled: selectedCount === 0,
    });
  }

  // Reorder — requires layout level
  if (hasEditorLayout) {
    menuItems.push({ type: 'separator' });
    menuItems.push({
      icon: ArrowUp,
      label: 'Bring to Front',
      action: onBringToFront,
      disabled: selectedCount === 0,
    });
    menuItems.push({
      icon: ArrowDown,
      label: 'Send to Back',
      action: onSendToBack,
      disabled: selectedCount === 0,
    });
  }

  // Lock/Hide — requires layout level
  if (hasEditorLayout) {
    menuItems.push({ type: 'separator' });
    menuItems.push({
      icon: isLocked ? Unlock : Lock,
      label: isLocked ? 'Unlock' : 'Lock',
      action: isLocked ? onUnlock : onLock,
      disabled: selectedCount === 0,
    });
    menuItems.push({
      icon: isHidden ? Eye : EyeOff,
      label: isHidden ? 'Show' : 'Hide',
      action: isHidden ? onShow : onHide,
      disabled: selectedCount === 0,
    });
  }

  // Delete — requires full control
  if (hasEditorFull) {
    menuItems.push({ type: 'separator' });
    menuItems.push({
      icon: Trash2,
      label: 'Delete',
      action: onDelete,
      shortcut: 'Del',
      disabled: selectedCount === 0,
      danger: true,
    });
  }

  return (
    <div
      ref={menuRef}
      className="fixed bg-white border border-gray-200 rounded-lg shadow-lg py-2 min-w-48 z-50"
      style={{
        left: position.x,
        top: position.y,
      }}
    >
      {menuItems.map((item, index) => {
        if (item.type === 'separator') {
          return <div key={index} className="h-px bg-gray-200 my-1" />;
        }

        const Icon = item.icon as React.FC<{ className?: string }>;
        return (
          <button
            key={index}
            onClick={() => {
              if (!item.disabled) {
                (item.action as () => void)();
                onClose();
              }
            }}
            disabled={item.disabled as boolean}
            className={`w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed ${
              item.danger ? 'text-red-600 hover:bg-red-50' : 'text-gray-700'
            }`}
          >
            <div className="flex items-center space-x-3">
              <Icon className="w-4 h-4" />
              <span>{item.label as string}</span>
            </div>
            {item.shortcut && (
              <span className="text-xs text-gray-400">{item.shortcut as string}</span>
            )}
          </button>
        );
      })}
      
      {selectedCount > 1 && (
        <>
          <div className="h-px bg-gray-200 my-1" />
          <div className="px-3 py-2 text-xs text-gray-500 bg-gray-50">
            {selectedCount} blocks selected
          </div>
        </>
      )}
    </div>
  );
};

export default ContextMenu;
